import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Media, { isImage, isVideo, getThumbnail } from '../../models/Media';
import ReactPlayer from "react-player";
import { useForm } from "react-hook-form";
import { CactusLabelInput } from "../../components";
import useFormValidatorText from '../../hooks/validators/use-form-validator-text';
import MediaService from "../../services/use_case/media/MediaService";
import { useDispatch } from 'react-redux';
import * as actionCreator from "../../actions";
import MediaThumbnail from '../../models/MediaThumbnail';
import DetailLibraryInfo from '../DetailLibraryInfo';
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faInfoCircle,
    faPen,
    faDownload,
    faUser,
    faCalendarAlt,
    faGlobe,
    faFile,
    faCheck,
    faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';


export type DetailLibraryInputs = {
    name: string;
    description: string;
}

interface DetailLibraryProps {
    media: Media | null;
    disableOnClose?: (value: boolean) => void;
};

const DetailLibrary: React.FC<DetailLibraryProps> = (props: DetailLibraryProps) => {

    const [t] = useTranslation();

    const [navLink, setNavLink] = useState<number>(0);
    const [isUpdate, setIsUpdate] = useState<boolean>(false);
    const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
    const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState<boolean>(false);
    const [thumbnailGenerateSuccess, setThumbnailGenerateSuccess] = useState<boolean>(false);
    const [media, setMedia] = useState<Media | null>(null);
    const [isLoadingMedia, setIsLoadingMedia] = useState<boolean>(false);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const { handleSubmit, register, errors, reset } = useForm<DetailLibraryInputs>();

    const dispatch = useDispatch();
    const textValidator = register(useFormValidatorText());

    useEffect(() => {
        if (!props.media) return;
        setIsLoadingMedia(true);
        setThumbnailGenerateSuccess(false);
        new MediaService().show(props.media.id)
            .then(res => {
                setMedia(res.data.data);
                reset(res.data.data);
            })
            .catch(() => {
                setMedia(props.media);
            })
            .finally(() => setIsLoadingMedia(false));
    }, [props.media?.id]);

    useEffect(() => {
        reset(media ?? {});
        setUpdateSuccess(false);
    }, [navLink]);

    useEffect(() => {
        props.disableOnClose?.(isUpdate);
    }, [isUpdate]);

    const _handleOnSubmit = async (data: DetailLibraryInputs) => {
        try {
            setIsUpdate(true);
            setUpdateSuccess(false);
            let updatedData = {
                name: data.name,
                description: data.description,
            };
            await new MediaService().update(media!.id, updatedData);
            let newMedia: Media = {
                ...media!,
                ...updatedData,
            };
            setMedia(newMedia);
            dispatch(actionCreator.mediaUpdate(newMedia));
            setIsUpdate(false);
            setUpdateSuccess(true);
        } catch (error) {
            setIsUpdate(false);
        }
    };

    const _handleGenerateThumbnail = async () => {
        if (!media) return;
        try {
            setIsGeneratingThumbnail(true);
            setThumbnailGenerateSuccess(false);
            await new MediaService().generateThumbnail(media.id);
            setThumbnailGenerateSuccess(true);
        } catch (error) {
            // error handled by BaseService interceptors (iziToast)
        } finally {
            setIsGeneratingThumbnail(false);
        }
    };

    const _renderMetaRow = (icon: any, label: string, value: string | null | undefined) => {
        if (!value) return null;
        return (
            <div className="d-flex align-items-start mb-3">
                <FontAwesomeIcon
                    icon={icon}
                    size="sm"
                    style={{ color: '#7b7b7b', marginTop: '2px', marginRight: '10px', width: '14px', flexShrink: 0 }}
                />
                <div>
                    <div style={{ fontSize: '0.68rem', color: '#656465', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1px' }}>
                        {label}
                    </div>
                    <div style={{ fontSize: '0.84rem', color: '#d0d0d0', wordBreak: 'break-word', lineHeight: 1.4 }}>
                        {value}
                    </div>
                </div>
            </div>
        );
    };

    const _renderThumbnailsInfo = () => {
        if (!media || !media?.thumbnails?.length) return null;
        return (
            <>
                <p style={{ fontSize: '0.68rem', color: '#656465', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', marginTop: '20px' }}>
                    {t('mediaitem_thumbnails')}
                </p>
                {media?.thumbnails.map((thumbnail: MediaThumbnail) => (
                    <DetailLibraryInfo key={thumbnail.id} item={thumbnail} />
                ))}
            </>
        );
    };

    const _renderNavBody = () => {
        switch (navLink) {
            case 0:
                return (
                    <div className="px-3 py-3" style={{ overflowY: 'auto', maxHeight: 'calc(70vh - 155px)' }}>
                        <p style={{ fontSize: '0.68rem', color: '#656465', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                            {t('mediaitem_original')}
                        </p>
                        <DetailLibraryInfo key={media?.id} item={media && videoDuration !== null ? { ...media, file_duration: videoDuration } : media} withDuration={true} />

                        {_renderThumbnailsInfo()}

                        <div className="d-flex align-items-center mt-3">
                            <button
                                className="btn btn-cactus btn-sm d-inline-flex align-items-center"
                                onClick={_handleGenerateThumbnail}
                                disabled={isGeneratingThumbnail}
                            >
                                <FontAwesomeIcon
                                    icon={faSyncAlt}
                                    size="sm"
                                    spin={isGeneratingThumbnail}
                                    style={{ marginRight: '6px' }}
                                />
                                {t('mediaitem_generate_thumbnail')}
                            </button>
                            {thumbnailGenerateSuccess && (
                                <span
                                    className="ml-3 d-flex align-items-center"
                                    style={{ color: '#43a459', fontSize: '0.84rem' }}
                                >
                                    <FontAwesomeIcon icon={faCheck} size="sm" style={{ marginRight: '5px' }} />
                                    {t('mediaitem_generate_thumbnail_success')}
                                </span>
                            )}
                        </div>

                        <div className="mt-4">
                            <p style={{ fontSize: '0.68rem', color: '#656465', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px', borderTop: '1px solid #141314', paddingTop: '16px' }}>
                                {t('mediaitem_info')}
                            </p>
                            {_renderMetaRow(faFile, t('mediaitem_original_name'), media?.original_name)}
                            {_renderMetaRow(faUser, t('mediaitem_uploaded_by'), media?.user_name)}
                            {_renderMetaRow(
                                faCalendarAlt,
                                t('mediaitem_created_at'),
                                media?.created_at ? moment(media?.created_at).format('DD/MM/YYYY HH:mm') : null
                            )}
                            {_renderMetaRow(faGlobe, t('mediaitem_visibility'), media?.visibility)}
                        </div>

                        {media?.full_path && (
                            <a
                                href={media.full_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="btn btn-cactus btn-sm mt-1 d-inline-flex align-items-center"
                            >
                                <FontAwesomeIcon icon={faDownload} size="sm" style={{ marginRight: '6px' }} />
                                {t('Download')}
                            </a>
                        )}
                    </div>
                );
            case 1:
                return (
                    <form className="px-3 py-3" onSubmit={handleSubmit(_handleOnSubmit)}>
                        <CactusLabelInput
                            labelTitle={t('Name')}
                            inputAttributes={{
                                id: "name",
                                name: "name",
                                type: "text",
                                ref: textValidator,
                            }}
                            errorMessage={errors.name ? errors.name.message : null}
                        />

                        <CactusLabelInput
                            labelTitle={t('Description')}
                            inputAttributes={{
                                id: "description",
                                name: "description",
                                type: "text",
                                ref: textValidator,
                            }}
                            errorMessage={errors.description ? errors.description.message : null}
                        />

                        <div className="d-flex align-items-center mt-2">
                            <button
                                type="submit"
                                className="btn btn-cactus"
                                disabled={isUpdate}
                            >
                                {t('Update Model', { model: t('Media') })}
                            </button>
                            {updateSuccess && (
                                <span
                                    className="ml-3 d-flex align-items-center"
                                    style={{ color: '#43a459', fontSize: '0.84rem' }}
                                >
                                    <FontAwesomeIcon icon={faCheck} size="sm" style={{ marginRight: '5px' }} />
                                    {t('mediaitem_updated_success')}
                                </span>
                            )}
                        </div>
                    </form>
                );
        }

        return null;
    };

    if(isLoadingMedia) {
        return (
            <div className="d-flex justify-content-center align-items-center h-100 py-5">
                <FontAwesomeIcon icon={faSyncAlt} spin style={{ color: '#656465', fontSize: '1.4rem' }} />
            </div>
        );
    }

    return (
        <div className="row align-items-stretch" style={{ minHeight: '60vh' }}>

            {/* Media Preview */}
            <div className="col-12 col-md-8">
                <div
                    className="d-flex align-items-center justify-content-center h-100"
                    style={{ backgroundColor: '#0a0a0a', minHeight: '300px', padding: '24px' }}
                >
                    {isImage(media?.mime_type) ? (
                        <img
                            src={getThumbnail(media, "system_full_hd")}
                            alt={media?.name}
                            style={{ maxWidth: '100%', maxHeight: '68vh', objectFit: 'contain' }}
                        />
                    ) : null}
                    {isVideo(media?.mime_type) ? (
                        <ReactPlayer url={media?.full_path} width='100%' controls onDuration={(d) => setVideoDuration(d)} />
                    ) : null}
                </div>
            </div>

            {/* Side Panel */}
            <div
                className="col-12 col-md-4 px-0 d-flex flex-column"
                style={{ borderLeft: '1px solid #0a0a0a' }}
            >
                {/* Header: name, description, type badge */}
                <div className="px-3 pt-3 pb-2" style={{ borderBottom: '1px solid #141314', flexShrink: 0 }}>
                    <h5
                        className="mb-1 font-weight-bold"
                        style={{ color: '#d0d0d0', fontSize: '0.95rem', wordBreak: 'break-word', lineHeight: 1.4 }}
                    >
                        {media?.name}
                    </h5>
                    {media?.description ? (
                        <p className="mb-0" style={{ color: '#7b7b7b', fontSize: '0.78rem', lineHeight: 1.4 }}>
                            {media?.description}
                        </p>
                    ) : null}
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs" style={{ flexShrink: 0 }}>
                    <li className="nav-item">
                        <a
                            className={`nav-link d-flex align-items-center ${navLink === 0 ? 'active' : ''}`}
                            href="!#"
                            onClick={(e) => {
                                e.preventDefault();
                                if (!isUpdate) setNavLink(0);
                            }}
                        >
                            <FontAwesomeIcon icon={faInfoCircle} size="sm" style={{ marginRight: '6px' }} />
                            {t('Details')}
                        </a>
                    </li>
                    <li className="nav-item">
                        <a
                            className={`nav-link d-flex align-items-center ${navLink === 1 ? 'active' : ''}`}
                            href="!#"
                            onClick={(e) => {
                                e.preventDefault();
                                if (!isUpdate) setNavLink(1);
                            }}
                        >
                            <FontAwesomeIcon icon={faPen} size="sm" style={{ marginRight: '6px' }} />
                            {t('Update')}
                        </a>
                    </li>
                </ul>

                {/* Tab Content */}
                <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                     {_renderNavBody()}
                </div>
            </div>

        </div>
    );

};

export default React.memo(DetailLibrary);
